/**
 * The account situation, drawn once for two audiences.
 *
 * The ADV's screen and the customer's own show the same four things — the
 * credit line, what is to pay, the ageing, the schedule — and they must show
 * them identically. A customer telephoning about their balance is describing
 * what is on their screen to somebody looking at another one, and two
 * renderings of the same figure is how that call goes wrong.
 *
 * What differs is what surrounds them: the ADV can recompute the credit, the
 * customer can download the statement. Those arrive as optional actions rather
 * than as a `variant` prop, because a boolean that switches four behaviours at
 * once is a component with two implementations inside it.
 *
 * Every amount is a string all the way to `money()`. See `api.ts`.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { day, money, EM_DASH } from "@hardauto/core-i18n";
import { Badge, Card, DataTable, Meter, Stat, StatGrid, type Column } from "@hardauto/core-ui";

import type { AgeBucket, FinancialDocument, Statement } from "./api";
import { NAMESPACE } from "./i18n";
import { creditTone, STATUS_TONE } from "./status";
import "./screens/billing.css";

export interface StatementPanelsProps {
  statement: Statement;
  /**
   * The open documents, for the schedule.
   *
   * Passed in rather than read here: the two callers reach two different
   * routes for them — the ledger's is scoped to a customer by id, the
   * portal's is scoped by the session — and a component that fetched would
   * have to know which.
   */
  documents: readonly FinancialDocument[];
  documentsLoading?: boolean;
  documentsFailed?: boolean;
  onRetryDocuments?: () => void;
  /** Rendered in the schedule's header. A download, usually. */
  scheduleAction?: ReactNode;
  /** Rendered in the credit card's header. */
  creditAction?: ReactNode;
}

export function StatementPanels({
  statement,
  documents,
  documentsLoading = false,
  documentsFailed = false,
  onRetryDocuments,
  scheduleAction,
  creditAction,
}: StatementPanelsProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;

  const hasLimit = Number(statement.credit_limit) > 0;

  const ageingColumns: Column<AgeBucket>[] = [
    // The band's label is the server's own — `0-30`, `31-60`, `90+`. Not
    // translated: an accountant reads them as numbers, and a key per band
    // would have to change every time a band does.
    { key: "band", header: t("statement.ageing.band"), render: (row) => row.label },
    {
      key: "amount",
      header: t("statement.ageing.amount"),
      align: "end",
      render: (row) => <span className="hardauto-num">{money(row.amount, locale)}</span>,
    },
    {
      key: "count",
      header: t("statement.ageing.count"),
      align: "end",
      render: (row) => String(row.count),
    },
  ];

  const scheduleColumns: Column<FinancialDocument>[] = [
    {
      key: "number",
      header: t("column.number"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <code className="hardauto-billing__number">{row.number}</code>
          <span className="hardauto-cell__muted">{t(`kind.${row.kind}`)}</span>
        </div>
      ),
    },
    { key: "date", header: t("column.date"), render: (row) => day(row.issued_on, locale) },
    { key: "due", header: t("column.due"), render: (row) => day(row.due_on, locale) },
    {
      key: "total",
      header: t("column.total"),
      align: "end",
      render: (row) => <span className="hardauto-num">{money(row.total, locale)}</span>,
    },
    {
      key: "outstanding",
      header: t("column.outstanding"),
      align: "end",
      // The document's own signed figure, never `total - paid` recomputed
      // here: a credit note's is negative, which is what makes this column
      // add up to the balance above it.
      render: (row) => (
        <span className="hardauto-num hardauto-num--strong">{money(row.outstanding, locale)}</span>
      ),
    },
    {
      key: "status",
      header: t("column.status"),
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`status.${row.status}`)}</Badge>,
    },
  ];

  return (
    <div className="hardauto-stack">
      <div className="hardauto-billing__split">
        <Card title={t("statement.credit.title")} actions={creditAction}>
          <div className="hardauto-billing__credit">
            <div>
              <p className="hardauto-billing__label">{t("statement.credit.balance")}</p>
              <p className="hardauto-billing__figure">{money(statement.balance, locale)}</p>
            </div>
            <div className="hardauto-billing__credit-limit">
              <p className="hardauto-billing__label">{t("statement.credit.limit")}</p>
              <p className="hardauto-billing__limit">
                {hasLimit ? money(statement.credit_limit, locale) : t("statement.credit.unlimited")}
              </p>
            </div>
          </div>

          {hasLimit ? (
            <>
              <Meter
                block
                value={statement.used_pct}
                tone={creditTone(statement.used_pct)}
                label={t("statement.credit.meter")}
                text={t("statement.credit.used", { percent: `${statement.used_pct} %` })}
              />
              {/* Toned like the gauge, not always green. "Disponible 0,00" in
                  the colour of good news, on a customer 15 % over their
                  limit, is the screen contradicting itself. */}
              <p className="hardauto-billing__available" data-tone={creditTone(statement.used_pct)}>
                {t("statement.credit.available", { amount: money(statement.available, locale) })}
              </p>
            </>
          ) : null}

          <p className="hardauto-billing__note">{t("statement.credit.note")}</p>
        </Card>

        <Card title={t("statement.due.title")}>
          <StatGrid label={t("statement.due.title")}>
            <Stat
              label={t("statement.due.overdue")}
              value={money(statement.overdue, locale)}
              hint={t("statement.due.overdueCount", { count: statement.overdue_count })}
              tone={Number(statement.overdue) > 0 ? "danger" : "neutral"}
            />
            <Stat
              label={t("statement.due.notDue")}
              value={money(statement.not_due, locale)}
              hint={t("statement.due.notDueCount", { count: statement.not_due_count })}
            />
            <Stat
              label={t("statement.due.credits")}
              value={money(statement.credits, locale)}
              hint={t("statement.due.creditsCount", { count: statement.credit_count })}
              tone={Number(statement.credits) > 0 ? "success" : "neutral"}
            />
            {/* Shown whatever its value: money received against no particular
                document is the figure a customer disputes, and hiding it at
                zero would make its absence look like an omission. */}
            <Stat
              label={t("statement.due.onAccount")}
              value={money(statement.on_account, locale)}
              hint={t("statement.due.onAccountNote")}
            />
            <Stat
              label={t("statement.due.next")}
              value={statement.next_due_on ? money(statement.next_due_amount, locale) : EM_DASH}
              hint={
                statement.next_due_on
                  ? t("statement.due.nextOn", { date: day(statement.next_due_on, locale) })
                  : t("statement.due.nextNone")
              }
            />
          </StatGrid>
        </Card>
      </div>

      <Card title={t("statement.ageing.title")} flush>
        <DataTable<AgeBucket>
          caption={t("statement.ageing.caption")}
          columns={ageingColumns}
          rows={statement.ageing}
          rowKey={(row) => row.label}
          empty={{ title: t("statement.ageing.empty") }}
          loadingLabel={t("statement.loading")}
        />
      </Card>

      <Card title={t("statement.schedule.title")} actions={scheduleAction} flush>
        <DataTable<FinancialDocument>
          caption={t("statement.schedule.caption")}
          columns={scheduleColumns}
          rows={documents}
          rowKey={(row) => row.id}
          loading={documentsLoading}
          loadingLabel={t("statement.loading")}
          error={
            documentsFailed
              ? {
                  title: t("error.title"),
                  description: t("error.body"),
                  ...(onRetryDocuments
                    ? { onRetry: onRetryDocuments, retryLabel: t("error.retry") }
                    : {}),
                }
              : undefined
          }
          empty={{ title: t("statement.schedule.empty") }}
        />
      </Card>
    </div>
  );
}
